import { lazy } from 'react'
import { createBrowserRouter, RouterProvider, Navigate, Outlet, redirect, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { Layout } from '@/components/layout/Layout'
import { RouteErrorElement } from '@/components/layout/RouteErrorBoundary'
// Imported from its source file, not the '@/features/auth' barrel: the
// barrel also re-exports LoginPage/SetPasswordPage/etc., which are lazy()
// below — a static import of the barrel here (for this one structural
// guard) would force the whole barrel (and every auth page) back into the
// main chunk, defeating those lazy() splits (INEFFECTIVE_DYNAMIC_IMPORT).
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute'
import { RequireRole } from '@/components/layout/RequireRole'
import { RouteSuspense } from '@/components/layout/RouteSuspense'
import { useAuthStore } from '@/stores'
import { monitorAutomationsRedirectTarget } from '@/utils/monitorRedirect'
import { usePersona } from '@/hooks/usePersona'
import { hubDefinitions } from '@/components/layout/navigation'
import { PLATFORM_ROLES } from '@/types/auth'

// FE-1 — these ~25 feature routes used to be statically imported (pulling
// every one of them, and their transitive deps, into the main chunk) while
// 49 sibling routes already used the lazy() + RouteSuspense pattern below.
// Converted to match: same pattern, same RouteSuspense wrapper, exact same
// route paths/guards preserved.
const LazyTrustPortalPage = lazy(() =>
  import('@/apps/trust-portal').then((m) => ({ default: m.TrustPortalPage }))
)
const LazyLoginPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.LoginPage }))
)
const LazySetPasswordPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.SetPasswordPage }))
)
const LazyAcceptOwnershipPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.AcceptOwnershipPage }))
)
const LazyForgotPasswordPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.ForgotPasswordPage }))
)
const LazyResetPasswordPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.ResetPasswordPage }))
)
const LazyTenantSelectionPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.TenantSelectionPage }))
)
const LazySSOCallbackErrorPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.SSOCallbackErrorPage }))
)
const LazyOAuthMCPReturnPage = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.OAuthMCPReturnPage }))
)
const LazyTenantsPage = lazy(() =>
  import('@/features/tenants').then((m) => ({ default: m.TenantsPage }))
)
const LazyTenantDetail = lazy(() =>
  import('@/features/tenants').then((m) => ({ default: m.TenantDetail }))
)
// P2.435 (UX-consult verdict): /tenant/build/workflows now mounts the
// Definitions facet shell — a `?kind=` segmented control over the existing
// per-kind list components (definitionKinds.ts) — instead of the bare
// workflows page directly. LazyWorkflowsPage itself is no longer imported
// here; DefinitionsPage lazy-loads WorkflowsPage (and the other three kinds)
// itself, per-facet.
const LazyDefinitionsPage = lazy(() =>
  import('@/components/build').then((m) => ({ default: m.DefinitionsPage }))
)
const LazyWorkflowDetail = lazy(() =>
  import('@/features/workflows').then((m) => ({ default: m.WorkflowDetail }))
)
const LazyFavoritesPage = lazy(() =>
  import('@/features/workflows').then((m) => ({ default: m.FavoritesPage }))
)
// Build-surfaces Stage 0 (BUILD-SURFACES-SIMPLIFICATION-SEP22.md §5): the
// tenant-visible glossary, reachable from the header's Support button.
const LazyGlossaryPage = lazy(() =>
  import('@/features/glossary').then((m) => ({ default: m.GlossaryPage }))
)
const LazyTagsPage = lazy(() =>
  import('@/features/workflows').then((m) => ({ default: m.TagsPage }))
)
const LazyCategoriesPage = lazy(() =>
  import('@/features/workflows').then((m) => ({ default: m.CategoriesPage }))
)
const LazyNewWorkflowPage = lazy(() =>
  import('@/features/workflows').then((m) => ({ default: m.NewWorkflowPage }))
)
const LazyExecutionDetail = lazy(() =>
  import('@/features/executions').then((m) => ({ default: m.ExecutionDetail }))
)
const LazyEngineRunDetailPage = lazy(() =>
  import('@/features/engine-runs').then((m) => ({ default: m.EngineRunDetailPage }))
)
// W11.2b (Addendum C.2/D.3) — kind-aware run detail shell, staged
// alongside the pre-existing per-engine LazyEngineRunDetailPage above.
const LazyMonitorRunDetailPage = lazy(() =>
  import('@/features/engine-runs').then((m) => ({ default: m.MonitorRunDetailPage }))
)
const LazyCostDashboard = lazy(() =>
  import('@/features/cost').then((m) => ({ default: m.CostDashboard }))
)
const LazyCostCentersPage = lazy(() =>
  import('@/features/cost').then((m) => ({ default: m.CostCentersPage }))
)
const LazyChargebackPage = lazy(() =>
  import('@/features/cost').then((m) => ({ default: m.ChargebackPage }))
)
const LazyReportsPage = lazy(() =>
  import('@/features/cost').then((m) => ({ default: m.ReportsPage }))
)
// ADR-239 Phase 1 — platform-console pages (grant-free metadata/totals
// views) replacing the tenant pages above under /platform/cost/*.
const LazyPlatformChargebackSummary = lazy(() =>
  import('@/features/cost').then((m) => ({ default: m.PlatformChargebackSummary }))
)
const LazyPlatformCostCentersSummary = lazy(() =>
  import('@/features/cost').then((m) => ({ default: m.PlatformCostCentersSummary }))
)
const LazyPlatformReportsPage = lazy(() =>
  import('@/features/cost').then((m) => ({ default: m.PlatformReportsPage }))
)
const LazyLicenseDashboard = lazy(() =>
  import('@/features/license').then((m) => ({ default: m.LicenseDashboard }))
)
const LazyNodeUsageDashboard = lazy(() =>
  import('@/features/license').then((m) => ({ default: m.NodeUsageDashboard }))
)
const LazyPlatformDashboard = lazy(() =>
  import('@/features/platform').then((m) => ({ default: m.PlatformDashboard }))
)
const LazyAuditLogViewer = lazy(() =>
  import('@/features/audit').then((m) => ({ default: m.AuditLogViewer }))
)
// ADR-239 Phase 1 — platform-console page (grant-free 7-field metadata
// list) replacing AuditLogViewer under /platform/audit.
const LazyPlatformAuditPage = lazy(() =>
  import('@/features/audit').then((m) => ({ default: m.PlatformAuditPage }))
)
const LazyAuditExportPage = lazy(() =>
  import('@/features/audit').then((m) => ({ default: m.AuditExportPage }))
)
const LazyUsersPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UsersPage }))
)
const LazyRolesPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.RolesPage }))
)
const LazyPlatformUsersPage = lazy(() =>
  import('@/features/platform-users').then((m) => ({ default: m.PlatformUsersPage }))
)
const LazyConnectorStoreSettingsPage = lazy(() =>
  import('@/features/platform-connector-store').then((m) => ({ default: m.ConnectorStoreSettingsPage }))
)
const LazyArchivalSettingsPage = lazy(() =>
  import('@/features/platform-archival-settings').then((m) => ({ default: m.ArchivalSettingsPage }))
)
const LazyPlatformEmailPage = lazy(() =>
  import('@/features/platform-email').then((m) => ({ default: m.PlatformEmailPage }))
)
const LazyPlatformFlagsPage = lazy(() =>
  import('@/features/platform-flags').then((m) => ({ default: m.PlatformFlagsPage }))
)
const LazyPlatformSecretsPage = lazy(() =>
  import('@/features/platform-secrets').then((m) => ({ default: m.PlatformSecretsPage }))
)
const LazyPlatformTemplateCatalogPage = lazy(() =>
  import('@/features/platform-template-catalog').then((m) => ({ default: m.TemplateCatalogReviewPage }))
)
const LazyGlobalTemplatesPage = lazy(() =>
  import('@/features/platform-global-templates').then((m) => ({ default: m.GlobalTemplatesPage }))
)
const LazyGlobalTemplateReviewPage = lazy(() =>
  import('@/features/platform-global-templates').then((m) => ({ default: m.GlobalTemplateReviewPage }))
)
const LazyImpersonationPage = lazy(() =>
  import('@/features/impersonation').then((m) => ({ default: m.ImpersonationPage }))
)
const LazyCredentialsPage = lazy(() =>
  import('@/features/credentials').then((m) => ({ default: m.CredentialsPage }))
)
const LazySettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.SettingsPage }))
)
const LazyTemplatesPage = lazy(() =>
  import('@/features/templates').then((m) => ({ default: m.TemplatesPage }))
)
const LazyTemplateDetail = lazy(() =>
  import('@/features/templates').then((m) => ({ default: m.TemplateDetail }))
)
// UseCasesPage (P2.426, reworked P2.431) — the AI hub's "Use cases" tab, now
// the ONE surface for automations (Overview/Drafts/History views inside the
// page, driven by ?view=). /tenant/build/templates/deployments (the old
// standalone deployment audit page) now just redirects here — see the route
// definition below — rather than mounting a second component.
const LazyUseCasesPage = lazy(() =>
  import('@/features/automation-blueprints').then((m) => ({ default: m.UseCasesPage }))
)
const LazyApprovalQueue = lazy(() =>
  import('@/features/approvals').then((m) => ({ default: m.ApprovalQueue }))
)
const LazyPlatformApprovalQueue = lazy(() =>
  import('@/features/approvals').then((m) => ({ default: m.PlatformApprovalQueue }))
)
// Imported from its source file, not the '@/features/search' barrel.
// Header.tsx used to statically import `CompactSearchBar` from this same
// barrel (Header renders on every authenticated page), which forced the
// barrel — and SearchPage along with it — into the main chunk regardless of
// how it was imported here (INEFFECTIVE_DYNAMIC_IMPORT). Header.tsx now
// imports CompactSearchBar from its own source file instead, so the barrel
// has no remaining static importer; this stays pinned to the source file
// for the same reason ProtectedRoute is above — importing feature pages
// directly from their own file, not the barrel, is the safe default here.
const LazySearchPage = lazy(() =>
  import('@/features/search/components/SearchPage').then((m) => ({ default: m.SearchPage }))
)
const LazyClustersPage = lazy(() =>
  import('@/features/clusters').then((m) => ({ default: m.ClustersPage }))
)
const LazyAgentOfflineLimitsPage = lazy(() =>
  import('@/features/clusters').then((m) => ({ default: m.AgentOfflineLimitsPage }))
)
const LazyClusterDetail = lazy(() =>
  import('@/features/clusters').then((m) => ({ default: m.ClusterDetail }))
)
const LazyManagedClustersPage = lazy(() =>
  import('@/features/managed-clusters').then((m) => ({ default: m.ManagedClustersPage }))
)
const LazyManagedClusterDetail = lazy(() =>
  import('@/features/managed-clusters').then((m) => ({ default: m.ManagedClusterDetail }))
)
const LazyDashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage }))
)
const LazyOnboardingWizard = lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.OnboardingWizard }))
)
const LazySSOSettingsPage = lazy(() =>
  import('@/features/sso-settings').then((m) => ({ default: m.SSOSettingsPage }))
)
const LazySSODriftReport = lazy(() =>
  import('@/features/sso-settings').then((m) => ({ default: m.SSODriftReport }))
)
const LazySendCampaignsPage = lazy(() =>
  import('@/features/send-campaigns').then((m) => ({ default: m.SendCampaignsPage }))
)
const LazyScheduledSendFlow = lazy(() =>
  import('@/features/send-campaigns').then((m) => ({ default: m.ScheduledSendFlow }))
)
const LazyOutboundChannelsPage = lazy(() =>
  import('@/features/outbound-channels').then((m) => ({ default: m.OutboundChannelsPage }))
)
const LazyDSRManagementPage = lazy(() =>
  import('@/features/sso-settings').then((m) => ({ default: m.DSRManagementPage }))
)
const LazyAccessReviewPage = lazy(() =>
  import('@/features/sso-settings').then((m) => ({ default: m.AccessReviewPage }))
)
const LazyTeamList = lazy(() =>
  import('@/features/teams').then((m) => ({ default: m.TeamList }))
)
const LazyTeamDetail = lazy(() =>
  import('@/features/teams').then((m) => ({ default: m.TeamDetail }))
)
const LazyCouncilList = lazy(() =>
  import('@/features/councils').then((m) => ({ default: m.CouncilList }))
)
const LazyCouncilDetail = lazy(() =>
  import('@/features/councils').then((m) => ({ default: m.CouncilDetail }))
)
const LazyCouncilSessionView = lazy(() =>
  import('@/features/councils').then((m) => ({ default: m.CouncilSessionView }))
)
const LazyDataHub = lazy(() =>
  import('@/components/layout/hubs/DataHub').then((m) => ({ default: m.DataHub }))
)
const LazyBuildHub = lazy(() =>
  import('@/components/layout/hubs/BuildHub').then((m) => ({ default: m.BuildHub }))
)
const LazyAIHub = lazy(() =>
  import('@/components/layout/hubs/AIHub').then((m) => ({ default: m.AIHub }))
)
const LazyGovernanceHub = lazy(() =>
  import('@/components/layout/hubs/GovernanceHub').then((m) => ({ default: m.GovernanceHub }))
)
const LazyInfraHub = lazy(() =>
  import('@/components/layout/hubs/InfraHub').then((m) => ({ default: m.InfraHub }))
)
const LazyAdminHub = lazy(() =>
  import('@/components/layout/hubs/AdminHub').then((m) => ({ default: m.AdminHub }))
)
const LazyDeletedHub = lazy(() =>
  import('@/components/layout/hubs/DeletedHub').then((m) => ({ default: m.DeletedHub }))
)
const LazyAccessHub = lazy(() =>
  import('@/components/layout/hubs/AccessHub').then((m) => ({ default: m.AccessHub }))
)
const LazyPlatformCostHub = lazy(() =>
  import('@/components/layout/hubs/PlatformCostHub').then((m) => ({ default: m.PlatformCostHub }))
)
const LazyPlatformAdminHub = lazy(() =>
  import('@/components/layout/hubs/PlatformAdminHub').then((m) => ({ default: m.PlatformAdminHub }))
)

// AF2 / ADR-232: the engine-console availability gate. Imported eagerly, not
// lazily: it is a few KB, and it has to be able to render the "not available"
// page WITHOUT pulling in the console chunk it is gating.

const LazyAirflowPage = lazy(() =>
  import('@/features/airflow').then((m) => ({ default: m.AirflowPage }))
)

// Item 8 (third-party notices): the underlying oss-licenses.txt mirror is
// ~2MB, so the whole page (which fetches it at runtime, not at build time)
// is lazy — it must never land in the initial bundle.
const LazyOpenSourceLicensesPage = lazy(() =>
  import('@/features/oss-licenses').then((m) => ({ default: m.OpenSourceLicensesPage }))
)

const LazyDagsterPage = lazy(() =>
  import('@/features/dagster').then((m) => ({ default: m.DagsterPage }))
)


const LazyAutomationsPage = lazy(() =>
  import('@/features/automations').then((m) => ({ default: m.AutomationsPage }))
)

// Lazy-loaded features — loaded on navigation, not in the initial bundle
const LazyRoutingRulesListPage = lazy(() =>
  import('@/features/notification-channels/components/RoutingRulesList').then((m) => ({ default: m.RoutingRulesListPage }))
)
const LazyRuleBuilderCreatePage = lazy(() =>
  import('@/features/notification-channels/components/RulesBuilder').then((m) => ({ default: m.RuleBuilderCreatePage }))
)
const LazyRuleBuilderEditPage = lazy(() =>
  import('@/features/notification-channels/components/RulesBuilder').then((m) => ({ default: m.RuleBuilderEditPage }))
)
const LazyNotificationFeedPage = lazy(() =>
  import('@/features/notification-channels/components/NotificationFeed').then((m) => ({ default: m.NotificationFeedPage }))
)
const LazyBroadcastComposerPage = lazy(() =>
  import('@/features/notification-channels/components/BroadcastComposer').then((m) => ({ default: m.BroadcastComposerPage }))
)
const LazyNotificationChannels = lazy(() =>
  import('@/features/notification-channels/components/ChannelMarketplace').then((m) => ({ default: m.ChannelMarketplace }))
)
const LazyChannelWizard = lazy(() =>
  import('@/features/notification-channels/components/ChannelWizard').then((m) => ({ default: m.ChannelWizard }))
)
const LazyConnectionsPage = lazy(() =>
  import('@/features/connections').then((m) => ({ default: m.ConnectionsPage }))
)
const LazyTenantSecretsPage = lazy(() =>
  import('@/features/tenant-secrets').then((m) => ({ default: m.TenantSecretsPage }))
)
const LazySecretBackendsPage = lazy(() =>
  import('@/features/secret-backends').then((m) => ({ default: m.SecretBackendsPage }))
)
const LazyArchivalConfigPage = lazy(() =>
  import('@/features/archival-config').then((m) => ({ default: m.ArchivalConfigPage }))
)
const LazyChatPage = lazy(() =>
  import('@/features/chat').then((m) => ({ default: m.ChatPage }))
)
const LazyMCPPage = lazy(() =>
  import('@/features/mcp').then((m) => ({ default: m.MCPPage }))
)
const LazyChooseToolsPage = lazy(() =>
  import('@/features/mcp').then((m) => ({ default: m.ChooseToolsPage }))
)
const LazyCatalogDetail = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.CatalogDetail }))
)
const LazyLibraryPage = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.LibraryPage }))
)
const LazyGuardrailsPage = lazy(() =>
  import('@/features/guardrails').then((m) => ({ default: m.GuardrailsPage }))
)
const LazyApprovalGatesPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.ApprovalGatesPage }))
)
const LazyCapabilitiesPage = lazy(() =>
  import('@/features/capabilities').then((m) => ({ default: m.CapabilitiesPage }))
)
const LazyAIModelsPage = lazy(() =>
  import('@/features/ai-models').then((m) => ({ default: m.AIModelsPage }))
)
const LazyGovernanceDashboard = lazy(() =>
  import('@/features/ai-governance').then((m) => ({ default: m.GovernanceDashboard }))
)
const LazyCompliancePage = lazy(() =>
  import('@/features/compliance').then((m) => ({ default: m.CompliancePage }))
)
const LazySensitiveFieldsPage = lazy(() =>
  import('@/features/sensitive-fields').then((m) => ({ default: m.SensitiveFieldsPage }))
)
const LazyTrustPage = lazy(() =>
  import('@/features/trust').then((m) => ({ default: m.TrustPage }))
)
const LazyLogArchivePage = lazy(() =>
  import('@/features/log-archive').then((m) => ({ default: m.LogArchivePage }))
)
const LazyPromptsPage = lazy(() =>
  import('@/features/prompts').then((m) => ({ default: m.PromptsPage }))
)
const LazyPromptDetail = lazy(() =>
  import('@/features/prompts').then((m) => ({ default: m.PromptDetail }))
)
const LazyKnowledgeBasesPage = lazy(() =>
  import('@/features/knowledge-bases').then((m) => ({ default: m.KnowledgeBasesPage }))
)
const LazyOutboundPlaybooksPage = lazy(() =>
  import('@/features/outbound-playbooks').then((m) => ({ default: m.OutboundPlaybooksPage }))
)
const LazyLeadsPage = lazy(() =>
  import('@/features/leads').then((m) => ({ default: m.LeadsPage }))
)
const LazyEventsPage = lazy(() =>
  import('@/features/events').then((m) => ({ default: m.EventsPage }))
)
const LazyChainDetailPage = lazy(() =>
  import('@/features/chains').then((m) => ({ default: m.ChainDetailPage }))
)
const _LazyPipelinesPage = lazy(() =>
  import('@/features/pipelines').then((m) => ({ default: m.PipelinesPage }))
)
const _LazyPipelineFullEditor = lazy(() =>
  import('@/features/pipelines').then((m) => ({ default: m.PipelineFullEditor }))
)
const LazyAgentList = lazy(() =>
  import('@/features/agents').then((m) => ({ default: m.AgentList }))
)
const LazyCatalogToolEditorPage = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.CatalogToolEditorPage }))
)
const LazyIdentifierDeclarationPage = lazy(() =>
  import('@/features/identifier-declarations').then((m) => ({ default: m.IdentifierDeclarationPage }))
)
const LazyIdentifierDeclarationReviewPage = lazy(() =>
  import('@/features/identifier-declarations').then((m) => ({ default: m.IdentifierDeclarationReviewPage }))
)
const LazyPlatformCatalogAdmin = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.PlatformCatalogAdmin }))
)
const LazyMarketplaceRegistriesAdmin = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.MarketplaceRegistriesAdmin }))
)
const LazyPlatformCatalogHub = lazy(() =>
  import('@/components/layout/hubs/PlatformCatalogHub').then((m) => ({ default: m.PlatformCatalogHub }))
)
// P1.7: platform-admin CRUD for connector_types (the shared catalog P2.1's
// remote-exec/DB connector types will be authored through).
const LazyConnectorTypeAdmin = lazy(() =>
  import('@/features/connector-types').then((m) => ({ default: m.ConnectorTypeAdminPage }))
)
const LazyAgentDetail = lazy(() =>
  import('@/features/agents').then((m) => ({ default: m.AgentDetail }))
)
const LazySkillList = lazy(() =>
  import('@/features/agents').then((m) => ({ default: m.SkillList }))
)
const LazySkillDetail = lazy(() =>
  import('@/features/agents').then((m) => ({ default: m.SkillDetail }))
)
const LazyStateGraphDetailPage = lazy(() =>
  import('@/features/stategraphs').then((m) => ({ default: m.StateGraphDetailPage }))
)
const LazyStateGraphCreatePage = lazy(() =>
  import('@/features/stategraphs').then((m) => ({ default: m.StateGraphCreatePage }))
)
const LazyStateGraphExecutionPage = lazy(() =>
  import('@/features/stategraphs').then((m) => ({ default: m.StateGraphExecutionPage }))
)
const LazyGenerateGraphPage = lazy(() =>
  import('@/features/stategraphs').then((m) => ({ default: m.GenerateGraphPage }))
)
const LazyChainCreatePage = lazy(() =>
  import('@/features/chains').then((m) => ({ default: m.ChainCreatePage }))
)
const LazyGenerateChainPage = lazy(() =>
  import('@/features/stategraphs').then((m) => ({ default: m.GenerateChainPage }))
)
const LazyGenerationRunPage = lazy(() =>
  import('@/features/stategraphs').then((m) => ({ default: m.GenerationRunPage }))
)
const LazyAIApprovalsPage = lazy(() =>
  import('@/features/stategraphs').then((m) => ({ default: m.AIApprovalsPage }))
)
const LazyRunbookCreatePage = lazy(() =>
  import('@/features/runbooks').then((m) => ({ default: m.RunbookCreatePage }))
)
const LazyRunbookDetailPage = lazy(() =>
  import('@/features/runbooks').then((m) => ({ default: m.RunbookDetailPage }))
)
const LazyExecutionDetailPage = lazy(() =>
  import('@/features/runbooks').then((m) => ({ default: m.ExecutionDetailPage }))
)
const LazyLoopDetailPage = lazy(() =>
  import('@/features/loops').then((m) => ({ default: m.LoopDetailPage }))
)
const LazyScheduleRedirect = lazy(() =>
  import('@/features/schedules/components/ScheduleRedirect').then((m) => ({ default: m.ScheduleRedirect }))
)
const LazyEnvironmentsPage = lazy(() =>
  import('@/features/environments').then((m) => ({ default: m.EnvironmentsPage }))
)
const LazyCreateTicketPage = lazy(() =>
  import('@/features/support').then((m) => ({ default: m.CreateTicketPage }))
)
const LazyMyTicketsPage = lazy(() =>
  import('@/features/support').then((m) => ({ default: m.MyTicketsPage }))
)
const LazyTicketDetailPage = lazy(() =>
  import('@/features/support').then((m) => ({ default: m.TicketDetailPage }))
)

// PLATFORM_ROLES now comes from types/auth.ts, derived from ROLE_PERMISSIONS
// through the same isPlatformRole() usePersona uses. It used to be a literal
// four-role array here, which disagreed with the persona hook for the other 15
// platform roles — see that constant's doc comment for what that cost.

function SmartRedirect() {
  const user = useAuthStore((s) => s.user)
  const isPlatformUser = user && PLATFORM_ROLES.includes(user.role)
  return <Navigate to={isPlatformUser ? '/platform' : '/tenant/dashboard'} replace />
}

// Exported so tests can exercise the REAL guard against a router (e.g.
// AIBillingKeysSection.gatewayLink.test.tsx, which proves the "Open AI
// Gateway"/"Open AI Providers" pointer link is actually reachable for both
// viewer contexts) instead of a hand-duplicated copy of this redirect logic
// that could silently drift from what App.tsx actually does.
export function TenantOnly({ children }: { children?: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  if (user && PLATFORM_ROLES.includes(user.role)) {
    return <Navigate to="/platform" replace />
  }
  return children ? <>{children}</> : <Outlet />
}

/**
 * Lands a hub's bare path on the first tab the ARRIVING ROLE can see.
 *
 * Replaces six static `<Navigate to="<tab>">` index routes. A fixed target is
 * only correct while every role can see that tab, and that kept not holding:
 * /tenant/admin pointed at License, which tenant_viewer's own license:read
 * denies, and narrowing the Airflow and Clusters tabs to the roles that hold
 * their (enforced) read grants broke /tenant/data and /tenant/infra the same
 * way. navigation.rbac.test.tsx asserts this property for every hub.
 *
 * hubDefinitions is the single source of truth, so a tab's gate and the landing
 * decision can no longer disagree.
 */
function HubIndexRedirect({ hubTo }: { hubTo: string }) {
  const persona = usePersona()
  const hub = hubDefinitions.find((h) => h.hubTo === hubTo)
  const first = hub?.tabs.find((t) => t.visible(persona))
  // No reachable tab: the hub itself should already be hidden, so this is a
  // belt-and-braces landing rather than an expected path.
  return <Navigate to={first ? first.to : '/tenant/dashboard'} replace />
}

function PlatformOnly({ children }: { children?: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  if (user && !PLATFORM_ROLES.includes(user.role)) {
    return <Navigate to="/tenant/dashboard" replace />
  }
  return children ? <>{children}</> : <Outlet />
}

// LegacyRedirect: every "old URL → new URL" detail redirect below must carry
// the query string and hash through. Deep links from the runs dashboard
// (chain: ?tab=executions&execution=<id>; agent: ?tab=test&execution=<id>)
// are FROZEN projector contracts, and a bare `<Navigate to={path}>` silently
// dropped them — live 2026-09-06, every chain/agent run landed on the
// resource's default tab. One helper, one rule, no per-redirect drift.
export function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation()
  return <Navigate to={`${to}${search}${hash}`} replace />
}
function RedirectTenantDetail() {
  const { id } = useParams<{ id: string }>()
  return <LegacyRedirect to={`/platform/tenants/${id}`} />
}

// Redirect helpers that preserve the :id param so deep links keep working.
function RedirectWorkflowDetail() {
  const { id } = useParams<{ id: string }>()
  return <LegacyRedirect to={`/tenant/build/workflows/${id}`} />
}
function RedirectAgentGraphDetail() {
  const { defID } = useParams<{ defID: string }>()
  return <LegacyRedirect to={`/tenant/build/agent-graphs/${defID}`} />
}
function RedirectAgentGraphExecution() {
  const { defID, execID } = useParams<{ defID: string; execID: string }>()
  return <LegacyRedirect to={`/tenant/build/agent-graphs/${defID}/executions/${execID}`} />
}
function RedirectChainDetail() {
  const { chainId } = useParams<{ chainId: string }>()
  return <LegacyRedirect to={`/tenant/build/chains/${chainId}`} />
}
function RedirectRunbookDetail() {
  const { id } = useParams<{ id: string }>()
  return <LegacyRedirect to={`/tenant/build/runbooks/${id}`} />
}
function RedirectRunbookExecution() {
  const { runbookId, execId } = useParams<{ runbookId: string; execId: string }>()
  return <LegacyRedirect to={`/tenant/build/runbooks/${runbookId}/executions/${execId}`} />
}
function RedirectTemplateDetail() {
  const { id } = useParams<{ id: string }>()
  return <LegacyRedirect to={`/tenant/build/templates/${id}`} />
}
function RedirectAgentDetail() {
  const { id } = useParams<{ id: string }>()
  return <LegacyRedirect to={`/tenant/build/agents/${id}`} />
}
function RedirectSkillDetail() {
  const { id } = useParams<{ id: string }>()
  return <LegacyRedirect to={`/tenant/build/skills/${id}`} />
}
function RedirectPromptDetail() {
  const { id } = useParams<{ id: string }>()
  return <LegacyRedirect to={`/tenant/ai/prompts/${id}`} />
}
function RedirectCouncilDetail() {
  const { councilId } = useParams<{ councilId: string }>()
  return <LegacyRedirect to={`/tenant/governance/council/${councilId}`} />
}
function RedirectCouncilSession() {
  const { councilId, sessionId } = useParams<{ councilId: string; sessionId: string }>()
  return <LegacyRedirect to={`/tenant/governance/council/${councilId}/sessions/${sessionId}`} />
}
function RedirectClusterDetail() {
  // M3: forward the query string — ClusterList/CredentialsPage deep-link with
  // ?tab=events / ?tab=settings which a bare redirect would strip.
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const qs = searchParams.toString()
  return <Navigate to={`/tenant/infra/clusters/${id}${qs ? `?${qs}` : ''}`} replace />
}
function RedirectTeamDetail() {
  // M2: preserve the team id (was dropping to the list page).
  const { teamId } = useParams<{ teamId: string }>()
  return <LegacyRedirect to={`/tenant/build/teams/${teamId}`} />
}
// W11.5b code-review P1 (2026-09-11): TeamProjector's frozen DeepLinkPath
// contract is "/tenant/teams/{teamID}/runs/{runID}" (automation/run.go),
// but TeamRunDetail has never been URL-addressed — it's an expand-toggle
// inside TeamDetail (see the `?run=` query param TeamDetail now reads to
// auto-expand). This redirect makes that frozen path resolve to a REAL
// page today instead of a dead link, mirroring RedirectRunbookExecution's
// pattern exactly; W11.5c/W11.2b re-homes both under Monitor later.
function RedirectTeamRun() {
  const { teamId, runId } = useParams<{ teamId: string; runId: string }>()
  const { search, hash } = useLocation()
  // Merge `run=` into any existing query string (not naive concatenation
  // like LegacyRedirect's — that would double up a leading "?" if the
  // frozen DeepLinkPath URL ever carries its own query string).
  const params = new URLSearchParams(search)
  if (runId) params.set('run', runId)
  if (runId && !params.has('tab')) params.set('tab', 'runs')
  const qs = params.toString()
  return <Navigate to={`/tenant/build/teams/${teamId}${qs ? `?${qs}` : ''}${hash}`} replace />
}
function RedirectApprovalDetail() {
  const { id } = useParams<{ id: string }>()
  return <LegacyRedirect to={`/tenant/governance/approvals/${id}`} />
}
function NotFound() {
  const user = useAuthStore((s) => s.user)
  const isPlatformUser = user && PLATFORM_ROLES.includes(user.role)
  const homePath = isPlatformUser ? '/platform' : '/tenant/dashboard'

  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900">404</h1>
        <p className="mt-4 text-lg text-gray-600">Page not found</p>
        <p className="mt-2 text-sm text-gray-500">The page you're looking for doesn't exist or has been moved.</p>
        <a
          href={homePath}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Go to {isPlatformUser ? 'Platform Dashboard' : 'Dashboard'}
        </a>
      </div>
    </div>
  )
}

// Exported (additive, P2.426) so App.routes.test.tsx can assert against the
// REAL route tree — that the AI hub's 'outbound-playbooks' and 'leads'
// child routes still resolve after the hardcoded sub-nav entries were
// replaced by the single data-driven "Use cases" tab — instead of a
// hand-duplicated copy of this config that could silently drift from it.
// eslint-disable-next-line react-refresh/only-export-components -- router is data, not a component; see App.routes.test.tsx
export const router = createBrowserRouter([
  // Public routes
  // These 8 are top-level router entries with no parent route to inherit an
  // errorElement from (the protected '/' root's errorElement below only
  // covers the authenticated tree) and no React error boundary wraps them
  // either (main.tsx renders <App/> directly) — so each needs its own
  // errorElement or a lazy chunk-load failure here (stale chunk after a
  // `kubectl set image` deploy; no cache-busting) falls through to
  // react-router's bare default error page. RouteErrorElement is already
  // chunk-aware (see RouteErrorBoundary.tsx).
  { path: '/trust/:tenantSlug', element: <RouteSuspense><LazyTrustPortalPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  { path: '/login', element: <RouteSuspense><LazyLoginPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  { path: '/select-tenant', element: <RouteSuspense><LazyTenantSelectionPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  { path: '/set-password', element: <RouteSuspense><LazySetPasswordPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  { path: '/accept-ownership', element: <RouteSuspense><LazyAcceptOwnershipPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  { path: '/forgot-password', element: <RouteSuspense><LazyForgotPasswordPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  { path: '/reset-password', element: <RouteSuspense><LazyResetPasswordPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  { path: '/sso/callback-error', element: <RouteSuspense><LazySSOCallbackErrorPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  // P2.280 (ADR-161): the MCP OAuth sign-in popup's dedicated return
  // route — internal/api/v1/mcpoauth's Callback handler redirects here.
  // Unauthenticated (same tier as the SSO callback routes above): its only
  // job is postMessage to window.opener + window.close(), no tenant data
  // is read or shown beyond the query params the backend already put in
  // the URL.
  { path: '/oauth/mcp/return', element: <RouteSuspense><LazyOAuthMCPReturnPage /></RouteSuspense>, errorElement: <RouteErrorElement /> },
  // Open Source Licenses: public, unauthenticated (compliance content, no
  // tenant data) — linked from the sidebar footer and Settings → Advanced.
  { path: '/oss-licenses', element: <RouteSuspense><LazyOpenSourceLicensesPage /></RouteSuspense> },

  // Protected root — Layout with sidebar/header
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      { index: true, element: <SmartRedirect /> },

      // Backward compat redirects for old /tenants routes
      { path: 'tenants', element: <Navigate to="/platform/tenants" replace /> },
      { path: 'tenants/:id', element: <RedirectTenantDetail /> },

      // Tenant-scoped routes
      {
        path: 'tenant',
        element: <TenantOnly />,
        children: [
          { index: true, element: <RouteSuspense><LazyDashboardPage /></RouteSuspense> },
          { path: 'dashboard', element: <RouteSuspense><LazyDashboardPage /></RouteSuspense> },
          { path: 'onboarding', element: <RouteSuspense><LazyOnboardingWizard /></RouteSuspense> },

          // Search stays at old path (used by header + deep links)
          { path: 'search', element: <RouteSuspense><LazySearchPage /></RouteSuspense> },

          // Chat moved INTO the AI hub (/tenant/ai/chat) so the AI nav item
          // stays highlighted and the hub tab bar stays visible in Chat. The
          // old standalone paths redirect, preserving session ids and query
          // params (?context=/&prompt=/&workflow_id= deep links from Airflow,
          // DeployModal, etc.).
          {
            path: 'chat',
            loader: ({ request }) => redirect(`/tenant/ai/chat${new URL(request.url).search}`),
          },
          {
            path: 'chat/:sessionId',
            loader: ({ params, request }) =>
              redirect(`/tenant/ai/chat/${params.sessionId}${new URL(request.url).search}`),
          },

          // Notifications — bell "view all" page stays at old path
          {
            path: 'notifications',
            element: <RouteSuspense><LazyNotificationFeedPage /></RouteSuspense>,
          },

          // Glossary (build-surfaces Stage 0) — reachable from the Support
          // button in the header, same idiom as 'support/*' below.
          { path: 'help/glossary', element: <RouteSuspense><LazyGlossaryPage /></RouteSuspense> },

          // Support — platform-side customer-support proxy pages
          { path: 'support', element: <Navigate to="/tenant/support/tickets" replace /> },
          {
            path: 'support/new',
            element: <RouteSuspense><LazyCreateTicketPage /></RouteSuspense>,
          },
          {
            path: 'support/tickets',
            element: <RouteSuspense><LazyMyTicketsPage /></RouteSuspense>,
          },
          {
            path: 'support/tickets/:id',
            element: <RouteSuspense><LazyTicketDetailPage /></RouteSuspense>,
          },

          // ── §7 redirects: Curate items ─────────────────────────────────────
          // Favorites → Build hub filtered by starred
          // N4: FavoritesPage stays routed (deep-link/palette only, out of nav)
          // until list-level star filters exist — a redirect to ?starred=1
          // would silently no-op since WorkflowsPage doesn't read it yet.
          { path: 'favorites', element: <RouteSuspense><LazyFavoritesPage /></RouteSuspense> },
          // Tags / Categories → Admin hub
          { path: 'tags', element: <Navigate to="/tenant/admin/tags" replace /> },
          { path: 'categories', element: <Navigate to="/tenant/admin/categories" replace /> },

          // ── §7 redirects: Build items (list pages only) ────────────────────
          // Old list routes redirect to canonical hub routes. Detail routes
          // are kept at old paths (deep links preserved) but ALSO mounted inside
          // the hub so breadcrumbs resolve correctly.
          { path: 'workflows', loader: () => redirect('/tenant/build/workflows') },
          { path: 'workflows/new', loader: () => redirect('/tenant/build/workflows/new') },
          { path: 'workflows/:id', element: <RedirectWorkflowDetail /> },
          { path: 'templates', loader: () => redirect('/tenant/build/templates') },
          { path: 'templates/:id', element: <RedirectTemplateDetail /> },
          { path: 'agents', loader: () => redirect('/tenant/build/agents') },
          { path: 'agents/skills', loader: () => redirect('/tenant/build/skills') },
          { path: 'agents/skills/:id', element: <RedirectSkillDetail /> },
          { path: 'agents/:id', element: <RedirectAgentDetail /> },
          { path: 'teams', loader: () => redirect('/tenant/build/teams') },
          { path: 'teams/new', loader: () => redirect('/tenant/build/teams') },
          { path: 'teams/:teamId', element: <RedirectTeamDetail /> },
          { path: 'teams/:teamId/runs/:runId', element: <RedirectTeamRun /> },
          { path: 'runbooks', loader: () => redirect('/tenant/build/runbooks') },
          { path: 'runbooks/new', loader: () => redirect('/tenant/build/runbooks/new') },
          { path: 'runbooks/:id', element: <RedirectRunbookDetail /> },
          { path: 'runbooks/:runbookId/executions/:execId', element: <RedirectRunbookExecution /> },
          { path: 'chains', loader: () => redirect('/tenant/build/chains') },
          { path: 'chains/:chainId', element: <RedirectChainDetail /> },
          { path: 'agent-graphs', loader: () => redirect('/tenant/build/agent-graphs') },
          { path: 'agent-graphs/new', loader: () => redirect('/tenant/build/agent-graphs/new') },
          { path: 'agent-graphs/:defID', element: <RedirectAgentGraphDetail /> },
          { path: 'agent-graphs/:defID/executions/:execID', element: <RedirectAgentGraphExecution /> },
          // skills shortcut (old direct alias)
          { path: 'skills', loader: () => redirect('/tenant/build/skills') },

          // ── §7 redirects: Pipelines → Chains (consolidated to hub path) ────
          { path: 'pipelines', loader: () => redirect('/tenant/build/chains?filter=pipeline') },
          { path: 'pipelines/new', loader: () => redirect('/tenant/build/chains?filter=pipeline') },
          {
            path: 'pipelines/:id',
            loader: ({ params }) => redirect(`/tenant/build/chains/${params.id}?filter=pipeline`),
          },

          // ── §7 redirects: stategraphs → agent-graphs (consolidated to hub) ─
          { path: 'stategraphs', loader: () => redirect('/tenant/build/agent-graphs') },
          { path: 'stategraphs/new', loader: () => redirect('/tenant/build/agent-graphs/new') },
          {
            path: 'stategraphs/:defID',
            loader: ({ params }) => redirect(`/tenant/build/agent-graphs/${params.defID}`),
          },
          {
            path: 'stategraphs/:defID/executions/:execID',
            loader: ({ params }) =>
              redirect(`/tenant/build/agent-graphs/${params.defID}/executions/${params.execID}`),
          },

          // ── §7 redirects: Monitor items (W11.2 — renamed from "Runs") ──────
          // /tenant/monitor is the Monitor hub canonical path (replaces the
          // old /tenant/automations — MONITOR-DOMAIN-DESIGN-SEP10.md).
          // Executions, Schedules, Loops, Calendars, Airflow, Dagster, Activity
          // redirect to /tenant/monitor with appropriate query params.
          // The sidebar hub is labelled "Monitor", so /tenant/runs (the natural
          // URL a user types or bookmarks) aliases to the canonical path.
          { path: 'runs', element: <Navigate to="/tenant/monitor" replace /> },
          // Cross-engine run detail (Runs hub deep-link fix, Sep 2026) —
          // path-segment-shaped: runs/airflow/{dagId}/{dagRunId},
          // runs/dagster/{runId}, runs/chat/{sessionId} (backend-landed
          // contract, commit 941406172). See
          // features/automations/utils/runFormatters.ts's resolveDeepLink
          // and features/engine-runs for the adapters + the canonical-header
          // GET /runs/{run_id} wiring (which derives its composite id from
          // these same segments client-side).
          { path: 'runs/:engine/*', element: <RouteSuspense><LazyEngineRunDetailPage /></RouteSuspense> },
          // W11.2b (Addendum C.2/D.3, staged) — kind-aware run detail shell.
          // Not yet the primary "All Runs" row destination for every kind
          // (resolveDeepLink is unchanged this pass — see
          // MonitorRunDetailPage.tsx's own doc comment); reachable today by
          // direct link while the shell is staged ahead of W11.5b/c adding
          // team/runbook/loop.
          { path: 'monitor/runs/:engine/*', element: <RouteSuspense><LazyMonitorRunDetailPage /></RouteSuspense> },
          { path: 'executions', element: <Navigate to="/tenant/monitor?tab=all" replace /> },
          { path: 'executions/:id', element: <RouteSuspense><LazyExecutionDetail /></RouteSuspense> },
          { path: 'schedules', element: <Navigate to="/tenant/monitor?tab=schedules" replace /> },
          // An old schedule address (a bookmark, an email): opens that schedule's editor in Monitor > Schedules.
          { path: 'schedules/:id', element: <RouteSuspense><LazyScheduleRedirect /></RouteSuspense> },
          { path: 'loops', element: <Navigate to="/tenant/monitor?tab=loops" replace /> },
          { path: 'loops/:id', element: <RouteSuspense><LazyLoopDetailPage /></RouteSuspense> },
          { path: 'calendars', element: <Navigate to="/tenant/monitor?tab=calendars" replace /> },
          // Airflow console — full DAG editor/monitor/XCom/GitSync/lineage/assets/config.
          // Per-run deep-links from airflow_projector.go:99 (?dag=&run=) land here.
          // ADR-244 (supersedes ADR-232 Part 1): the tenant opt-in capability
          // gate (EngineConsoleGate) is retired — clusters.engines is now the
          // only switch for turning an engine on. The route is bare, matching
          // every other engine route in this file; AirflowPage itself renders
          // an empty state (with an owner/admin-aware CTA to Settings →
          // Clusters) when no cluster carries the engine yet, and DAGList's
          // error state is state-aware (installing/failed/agent-not-connected)
          // instead of a hard gate in front of the whole page.
          // The consoles now live under the Data hub (/tenant/data/airflow);
          // this path stays as a redirect that PRESERVES the query string and
          // hash, because backend projectors emit ?dag=&run= / ?run= links
          // and the old palette/bookmarks use it (?edit=, ?tab=, ?cluster=).
          {
            path: 'airflow',
            loader: ({ request }) => {
              const u = new URL(request.url)
              return redirect(`/tenant/data/airflow${u.search}${u.hash}`)
            },
          },
          // Dagster console — assets/materializations/checks/lineage/analytics.
          // Per-run deep-links from dagster_projector.go:145 (?run=) land here.
          // ADR-244: same change as airflow above.
          {
            path: 'dagster',
            loader: ({ request }) => {
              const u = new URL(request.url)
              return redirect(`/tenant/data/dagster${u.search}${u.hash}`)
            },
          },
          // ── Data hub (/tenant/data/*) — Airflow and Dagster consoles ───────
          // ADR-244: no capability gate; each page renders its own empty
          // state when no cluster carries the engine.
          {
            path: 'data',
            element: <RouteSuspense><LazyDataHub /></RouteSuspense>,
            children: [
              { index: true, element: <HubIndexRedirect hubTo="/tenant/data" /> },
              { path: 'airflow', element: <RouteSuspense><LazyAirflowPage /></RouteSuspense> },
              { path: 'dagster', element: <RouteSuspense><LazyDagsterPage /></RouteSuspense> },
            ],
          },
          { path: 'activity', element: <Navigate to="/tenant/monitor?tab=activity" replace /> },
          // Routing rules → Infra notification channels (or admin sub-page per §7)
          {
            path: 'notifications/rules',
            element: <Navigate to="/tenant/admin/notification-rules" replace />,
          },
          {
            path: 'notifications/rules/new',
            element: <Navigate to="/tenant/admin/notification-rules/new" replace />,
          },
          {
            path: 'notifications/rules/:id/edit',
            element: <Navigate to="/tenant/admin/notification-rules" replace />,
          },

          // ── §7 redirects: AI items ─────────────────────────────────────────
          { path: 'library', loader: () => redirect('/tenant/ai/library') },
          { path: 'library/:slug', loader: ({ params }) => redirect(`/tenant/ai/library/${params.slug}`) },
          // catalog/marketplace → library (old compat, now hub-aware)
          { path: 'catalog', loader: () => redirect('/tenant/ai/library') },
          { path: 'catalog/:slug', loader: ({ params }) => redirect(`/tenant/ai/library/${params.slug}`) },
          { path: 'marketplace', loader: () => redirect('/tenant/ai/library?source=marketplace') },
          // mcp-tools shortcut and old /tenant/mcp path
          { path: 'mcp-tools', loader: () => redirect('/tenant/ai/mcp') },
          { path: 'mcp', loader: () => redirect('/tenant/ai/mcp') },
          // prompts list
          { path: 'prompts', loader: () => redirect('/tenant/ai/prompts') },
          { path: 'prompts/:id', element: <RedirectPromptDetail /> },
          // ai/models → ai/gateway; ai/governance → governance/ai
          { path: 'ai/models', loader: () => redirect('/tenant/ai/gateway') },
          { path: 'ai/governance', loader: () => redirect('/tenant/governance/ai') },

          // ── §7 redirects: Governance items ────────────────────────────────
          { path: 'guardrails', loader: () => redirect('/tenant/governance/guardrails') },
          { path: 'approvals', loader: () => redirect('/tenant/governance/approvals') },
          { path: 'approvals/:id', element: <RedirectApprovalDetail /> },
          { path: 'councils', loader: () => redirect('/tenant/governance/council') },
          { path: 'councils/:councilId', element: <RedirectCouncilDetail /> },
          {
            path: 'councils/:councilId/sessions/:sessionId',
            element: <RedirectCouncilSession />,
          },
          // B1: preserve the search string — AuditOverlay deep-links carry
          // ?resource_id= filters that a bare redirect() would silently drop.
          {
            path: 'audit',
            loader: ({ request }) => {
              const url = new URL(request.url)
              return redirect(`/tenant/governance/audit${url.search}`)
            },
          },
          {
            path: 'audit/export',
            loader: ({ request }) => {
              const url = new URL(request.url)
              return redirect(`/tenant/governance/audit/export${url.search}`)
            },
          },
          { path: 'dsr', loader: () => redirect('/tenant/governance/dsr') },

          // ── §7 redirects: Infrastructure items ────────────────────────────
          // ADR-244: preserve the search string, same as the audit redirects
          // above — EngineClusterEmptyState's CTA carries
          // ?intent=provision-<engine>, which a bare redirect() would
          // silently drop.
          {
            path: 'clusters',
            loader: ({ request }) => {
              const url = new URL(request.url)
              return redirect(`/tenant/infra/clusters${url.search}`)
            },
          },
          { path: 'clusters/:id', element: <RedirectClusterDetail /> },
          {
            path: 'branch-deployments',
            loader: () => redirect('/tenant/infra/environments'),
          },
          {
            path: 'credentials',
            loader: () => redirect('/tenant/infra/credentials'),
          },
          {
            path: 'environments',
            loader: () => redirect('/tenant/infra/environments'),
          },
          {
            path: 'connections',
            loader: () => redirect('/tenant/infra/connections'),
          },
          {
            path: 'secret-backends',
            loader: () => redirect('/tenant/infra/secret-backends'),
          },
          {
            path: 'notification-channels',
            loader: () => redirect('/tenant/infra/notification-channels'),
          },
          {
            path: 'notification-channels/new/:type',
            loader: ({ params }) =>
              redirect(`/tenant/infra/notification-channels/new/${params.type}`),
          },
          {
            path: 'notification-channels/:id/edit',
            loader: ({ params }) =>
              redirect(`/tenant/infra/notification-channels/${params.id}/edit`),
          },

          // ── §7 redirects: Admin items ──────────────────────────────────────
          { path: 'users', loader: () => redirect('/tenant/admin/users') },
          { path: 'license', loader: () => redirect('/tenant/admin/license') },

          // ── Monitor hub: canonical path is /tenant/monitor (W11.2 —
          // MONITOR-DOMAIN-DESIGN-SEP10.md; renamed from the "Runs" hub at
          // /tenant/automations). Today (the promoted Run Plan tab) is now
          // the default tab; Schedules/Loops/Calendars/System stay reachable
          // as tabs on this same hub in this pass (pass 2 moves them into
          // Build › Scheduling per the design doc's two-pass migration plan).
          {
            path: 'monitor',
            element: (
              <RouteSuspense>
                <LazyAutomationsPage
                  defaultTab="run-plan"
                  pageTitle="Monitor"
                  pageDescription="Today's workload, running/waiting/blocked/held executions, and cross-engine run history. Deep-link to engine consoles for engine-specific controls."
                />
              </RouteSuspense>
            ),
          },
          // ── /tenant/automations — old canonical path, now a redirect ───────
          // Drops `?tab=run-plan` (Today is the new default, no tab param
          // needed) and preserves every other query param verbatim (date,
          // tab=activity/all/schedules/loops/calendars/system, status,
          // period, engine, ...) so no bookmark loses its filter state.
          {
            path: 'automations',
            loader: ({ request }) => {
              const url = new URL(request.url)
              return redirect(monitorAutomationsRedirectTarget(url.search))
            },
          },
          // ── Dependent events console (ADR-141) ─────────────────────────────
          {
            path: 'events',
            element: <RouteSuspense><LazyEventsPage /></RouteSuspense>,
          },

          // ── Build hub (/tenant/build/*) ────────────────────────────────────
          {
            path: 'build',
            element: <RouteSuspense><LazyBuildHub /></RouteSuspense>,
            children: [
              // Default: redirect to Workflows tab
              { index: true, element: <HubIndexRedirect hubTo="/tenant/build" /> },
              // Workflows
              // P2.435: /tenant/build/workflows now mounts the Definitions
              // facet shell (kind switcher + unified Create menu) instead of
              // the bare WorkflowsPage — see definitionKinds.ts.
              { path: 'workflows', element: <RouteSuspense><LazyDefinitionsPage /></RouteSuspense> },
              // RBAC: create:workflow is held by tenant_owner/tenant_admin/
              // tenant_operator/platform_admin (role_matrix.golden). The
              // in-page links to this route were already gated on
              // canCreateWorkflow, but the command palette and a typed URL
              // were not — six tenant roles could open the full create form
              // and have the submit refused. Guarding the ROUTE closes every
              // entry point at once, and the dead-end check in
              // navigation.rbac.test.tsx can see a RequireRole.
              {
                path: 'workflows/new',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'tenant_operator', 'platform_admin']}>
                    <RouteSuspense><LazyNewWorkflowPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              { path: 'workflows/:id', element: <RouteSuspense><LazyWorkflowDetail /></RouteSuspense> },
              // Agent Graphs — the list page is now a facet of Definitions
              // (P2.435); detail/editor routes below are UNCHANGED.
              {
                path: 'agent-graphs',
                loader: () => redirect('/tenant/build/workflows?kind=agent_graph'),
              },
              {
                path: 'agent-graphs/new',
                element: <RouteSuspense><LazyStateGraphCreatePage /></RouteSuspense>,
              },
              // Build with AI: governed generation (plan review → preview → apply
              // approval). Static segments, declared before ':defID'.
              {
                path: 'agent-graphs/generate',
                element: <RouteSuspense><LazyGenerateGraphPage /></RouteSuspense>,
              },
              {
                path: 'agent-graphs/generate/:runID',
                element: <RouteSuspense><LazyGenerationRunPage /></RouteSuspense>,
              },
              // AI changes waiting at the final approval, for the second approver.
              // Static segment, declared before ':defID'.
              {
                path: 'agent-graphs/approvals',
                element: <RouteSuspense><LazyAIApprovalsPage /></RouteSuspense>,
              },
              // Send campaigns: one agent-graph run per recipient, paced by the send ramp (owner/admin; the API enforces the
              // same rule). Static segment, declared before ':defID'.
              {
                path: 'agent-graphs/campaigns',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin']}>
                    <RouteSuspense><LazySendCampaignsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // "Send on a schedule" (ADR-336, screen 21): a guided create for one graph. Owner/admin, like the campaign routes.
              {
                path: 'agent-graphs/:defID/schedule-send',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin']}>
                    <RouteSuspense><LazyScheduledSendFlow /></RouteSuspense>
                  </RequireRole>
                ),
              },
              {
                path: 'agent-graphs/:defID',
                element: <RouteSuspense><LazyStateGraphDetailPage /></RouteSuspense>,
              },
              {
                path: 'agent-graphs/:defID/executions/:execID',
                element: <RouteSuspense><LazyStateGraphExecutionPage /></RouteSuspense>,
              },
              // Chains — the list page is now a facet of Definitions
              // (P2.435); the detail route below is UNCHANGED.
              {
                path: 'chains',
                loader: () => redirect('/tenant/build/workflows?kind=chain'),
              },
              // Static segment, declared before ':chainId'.
              {
                path: 'chains/new',
                element: <RouteSuspense><LazyChainCreatePage /></RouteSuspense>,
              },
              // Build with AI for chains (governed generation). Static segments,
              // declared before ':chainId'.
              {
                path: 'chains/generate',
                element: <RouteSuspense><LazyGenerateChainPage /></RouteSuspense>,
              },
              {
                path: 'chains/generate/:runID',
                element: <RouteSuspense><LazyGenerationRunPage /></RouteSuspense>,
              },
              {
                path: 'chains/approvals',
                element: <RouteSuspense><LazyAIApprovalsPage kind="chain" /></RouteSuspense>,
              },
              {
                path: 'chains/:chainId',
                element: <RouteSuspense><LazyChainDetailPage /></RouteSuspense>,
              },
              // Runbooks — the list page is now a facet of Definitions
              // (P2.435); detail/editor routes below are UNCHANGED.
              {
                path: 'runbooks',
                loader: () => redirect('/tenant/build/workflows?kind=runbook'),
              },
              {
                // RBAC: this page serves BOTH the blank create form and the AI
                // draft flow (?ai=1), and the server's two gates differ —
                // internal/api/v1/runbook/handler.go checks roles in-handler
                // rather than via middleware, so neither role_matrix.golden nor
                // a routes.go grep shows either of them:
                //
                //   CreateRunbook  platform_admin / tenant_owner / tenant_admin
                //   DraftRunbook   the same PLUS tenant_operator
                //
                // So the route is guarded on the WIDER draft set: an operator
                // may legitimately draft here. The create submit is gated
                // separately inside RunbookCreatePage, and the two "New
                // Runbook" entry points (this page's list header and the
                // command palette) are gated on the narrower create set.
                // Guarding the route on the create set instead would have taken
                // AI drafting away from a role the server permits.
                path: 'runbooks/new',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'tenant_operator', 'platform_admin']}>
                    <RouteSuspense><LazyRunbookCreatePage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              {
                path: 'runbooks/:id',
                element: <RouteSuspense><LazyRunbookDetailPage /></RouteSuspense>,
              },
              {
                path: 'runbooks/:runbookId/executions/:execId',
                element: <RouteSuspense><LazyExecutionDetailPage /></RouteSuspense>,
              },
              // Templates
              { path: 'templates', element: <RouteSuspense><LazyTemplatesPage /></RouteSuspense> },
              // MUST precede templates/:id — otherwise "deployments" is
              // captured as a template id and TemplateDetail 404s (same
              // trap the agents/skills route below documents). P2.431: the
              // deployment audit log is now the "History" view on the
              // merged Use cases page — redirect rather than mount a
              // second, now-unused standalone page (same idiom as the
              // `automations` redirect below).
              {
                path: 'templates/deployments',
                loader: () => redirect('/tenant/ai/use-cases?view=history'),
              },
              { path: 'templates/:id', element: <RouteSuspense><LazyTemplateDetail /></RouteSuspense> },
              // Agents
              {
                path: 'agents',
                element: <RouteSuspense><LazyAgentList /></RouteSuspense>,
              },
              // MUST precede agents/:id — otherwise "skills" is captured as an
              // agent id and AgentDetail crashes on the missing agent (QA find).
              { path: 'agents/skills', element: <Navigate to="/tenant/build/skills" replace /> },
              {
                path: 'agents/:id',
                element: <RouteSuspense><LazyAgentDetail /></RouteSuspense>,
              },
              // Skills (Agents sub-hub)
              {
                path: 'skills',
                element: <RouteSuspense><LazySkillList /></RouteSuspense>,
              },
              {
                path: 'skills/:id',
                element: <RouteSuspense><LazySkillDetail /></RouteSuspense>,
              },
              // Teams (Agents sub-hub)
              { path: 'teams', element: <RouteSuspense><LazyTeamList /></RouteSuspense> },
              { path: 'teams/new', element: <RouteSuspense><LazyTeamList /></RouteSuspense> },
              { path: 'teams/:teamId', element: <RouteSuspense><LazyTeamDetail /></RouteSuspense> },
            ],
          },

          // ── AI hub (/tenant/ai/*) ──────────────────────────────────────────
          {
            path: 'ai',
            element: <RouteSuspense><LazyAIHub /></RouteSuspense>,
            children: [
              // Default: land on Chat (the AI section's primary surface)
              { index: true, element: <HubIndexRedirect hubTo="/tenant/ai" /> },
              // Chat — full-height child (AIHub switches to a h-full layout
              // on this path; Layout drops main's padding/scroll the same way
              // it did for the old /tenant/chat)
              {
                path: 'chat',
                element: <RouteSuspense><LazyChatPage /></RouteSuspense>,
              },
              {
                path: 'chat/:sessionId',
                element: <RouteSuspense><LazyChatPage /></RouteSuspense>,
              },
              // Library (absorbs catalog/marketplace)
              {
                path: 'library',
                element: <RouteSuspense><LazyLibraryPage /></RouteSuspense>,
              },
              {
                path: 'library/:slug',
                element: <RouteSuspense><LazyCatalogDetail /></RouteSuspense>,
              },
              // MCP Tools
              {
                path: 'mcp',
                element: <RouteSuspense><LazyMCPPage /></RouteSuspense>,
              },
              // Choose which of a connection's tools to use (ADR-330)
              {
                path: 'mcp/connections/:connectionId/choose-tools',
                element: <RouteSuspense><LazyChooseToolsPage /></RouteSuspense>,
              },
              // Prompts
              {
                path: 'prompts',
                element: <RouteSuspense><LazyPromptsPage /></RouteSuspense>,
              },
              {
                path: 'prompts/:id',
                element: <RouteSuspense><LazyPromptDetail /></RouteSuspense>,
              },
              // Knowledge Bases (ADR-204 Wave A package 1) — tenant RAG on
              // BYOK. Sits alongside Prompts/MCP Tools as another
              // AI-configuration resource the tenant owns; unlike those two,
              // it is additionally gated on feature.tenant_rag
              // (KnowledgeBasesPage itself renders the not-enabled state).
              {
                path: 'knowledge-bases',
                element: <RouteSuspense><LazyKnowledgeBasesPage /></RouteSuspense>,
              },
              // AI Gateway (formerly ai/models)
              {
                path: 'gateway',
                element: <RouteSuspense><LazyAIModelsPage /></RouteSuspense>,
              },
              // Outbound Playbook (P2.332, ADR-163 draft) — route KEPT (not
              // hidden) after P2.426 replaced its hardcoded AI sub-nav entry
              // with the generic "Use cases" tab, so existing deep links and
              // command-palette aliases keep working; the page itself gains
              // a "Use cases" back-link (OutboundPlaybooksPage.tsx).
              {
                path: 'outbound-playbooks',
                element: <RouteSuspense><LazyOutboundPlaybooksPage /></RouteSuspense>,
              },
              // Leads (P2.334, ADR-163 draft) — route KEPT, see comment above.
              {
                path: 'leads',
                element: <RouteSuspense><LazyLeadsPage /></RouteSuspense>,
              },
              // Use cases (P2.426, reworked P2.431): the ONE surface for
              // automations — Overview (deployed + available-to-deploy
              // cards), Drafts, and History (the deployment audit log,
              // formerly its own page at /tenant/build/templates/
              // deployments — see that route's redirect above and
              // LazyUseCasesPage's own comment) as ?view=-driven views.
              {
                path: 'use-cases',
                element: <RouteSuspense><LazyUseCasesPage /></RouteSuspense>,
              },
            ],
          },

          // ── Governance hub (/tenant/governance/*) ─────────────────────────
          {
            path: 'governance',
            element: <RouteSuspense><LazyGovernanceHub /></RouteSuspense>,
            children: [
              // Default: redirect to Approvals
              { index: true, element: <HubIndexRedirect hubTo="/tenant/governance" /> },
              // Approvals
              { path: 'approvals', element: <RouteSuspense><LazyApprovalQueue /></RouteSuspense> },
              { path: 'approvals/:id', element: <RouteSuspense><LazyApprovalQueue /></RouteSuspense> },
              // Approval Gates — thresholds/enforce-mode/second-eye settings
              // (APPROVALS.W3.F / docs/governance/SECOND-EYE-SOD.md). Role set
              // is everyone the SERVER will serve this page's reads to; the
              // page then narrows further per-control via useCapabilities
              // (approvals:toggle_enforce, approvals:manage_second_eye).
              //
              // approval_approver was removed from this list, deliberately
              // (ClickUp 14ypx624q68). It had been inert — the persona lives in
              // tenant_users.additional_roles and by convention is never the
              // base role, so RequireRole's base-role comparison could not
              // match it. Teaching the session to carry additional_roles made
              // it match, and the result was a page of error panels: GET
              // /tenant/approval-thresholds and all four retention GETs are
              // RequireRoleForResource(owner, admin, platform_admin), and
              // role_matrix.golden has no approval_threshold row for the
              // persona. Only the PUT admits it, which the UI cannot reach
              // without first rendering the current state.
              //
              // The server is self-inconsistent here (PUT admits, GETs do
              // not). Resolving that is a policy call: grant the persona
              // approval_threshold read in rbac.rego and the GET mounts, then
              // restore it here and in navigation.ts together.
              {
                path: 'approval-gates',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyApprovalGatesPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Capabilities — tenant self-service Tenant Capabilities page
              // (docs/planning/consults/flags-sep24/ux-tenant-scoped-flags.md
              // §1b/§2; DECISIONS.md ADR-201 addendum). Route-level: view is
              // owner/admin/platform_admin (matches approval-gates/guardrails'
              // role sets); per-row write eligibility is server-computed
              // (`can_write` on each row) and never re-derived client-side.
              {
                path: 'capabilities',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyCapabilitiesPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Guardrails (RequireRole added — page held raw violation
              // evidence with no route-level gate; mirrors the Connections/
              // Credentials settings-route role set. ViolationsTab evidence
              // + export are further narrowed by guardrails:view_violations.)
              {
                path: 'guardrails',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'tenant_operator', 'platform_admin']}>
                    <RouteSuspense><LazyGuardrailsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // AI Governance (RequireRole preserved from original route)
              {
                path: 'ai',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyGovernanceDashboard /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // AI Council
              { path: 'council', element: <RouteSuspense><LazyCouncilList /></RouteSuspense> },
              { path: 'council/:councilId', element: <RouteSuspense><LazyCouncilDetail /></RouteSuspense> },
              { path: 'council/:councilId/sessions/:sessionId', element: <RouteSuspense><LazyCouncilSessionView /></RouteSuspense> },
              // Audit Log
              { path: 'audit', element: <RouteSuspense><LazyAuditLogViewer /></RouteSuspense> },
              { path: 'audit/export', element: <RouteSuspense><LazyAuditExportPage /></RouteSuspense> },
              // Log Archive — LA Phase 3 Lane B: search/export of archived
              // task/execution logs (cold storage). Admin-tier, same
              // RequireRole set as Compliance/Trust below (mirrors the
              // backend's airflow_log_archive:read grant to
              // platform_admin/tenant_owner/tenant_admin only — design doc
              // §2 Security / §4 item 8).
              {
                path: 'logs',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyLogArchivePage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // DSR (RequireRole preserved from original route)
              {
                path: 'dsr',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyDSRManagementPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Sensitive Fields — tenant custom sensitive-field definitions
              // (ADR-118). Read is open to the four tenant roles in the list below —
              // NOT "all tenant roles", as this comment previously said. Five are
              // absent (billing_admin, finance_viewer, cost_center_admin,
              // approval_approver, approval_viewer), and the nav tab was showing
              // them a link this guard then rejected. Mutations are admin-gated
              // server-side + via useCapability in-page.
              {
                path: 'sensitive-fields',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'tenant_operator', 'tenant_viewer', 'platform_admin']}>
                    <RouteSuspense><LazySensitiveFieldsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Compliance — evidence packs + Art.22 coverage
              {
                path: 'compliance',
                element: (
                  // NOT widened for compliance:read or art22:manage, though
                  // both looked like the trust case. The route-level
                  // RequirePermission is not the only gate: every evidence,
                  // catalog and sources handler then calls
                  // evidencePackIsAuthorized (evidence_pack_handler.go:65),
                  // a role switch over tenant_owner/tenant_admin/
                  // platform_admin. A custom role presents its base role, so
                  // it passes the route and 403s in the handler — admitting
                  // it here would offer a tab whose every panel fails.
                  // art22:manage alone is no better: CompliancePage itself
                  // returns "no permission" unless compliance:read is held.
                  // Removing that role switch the way #19 did for art22 is a
                  // server change, and has to come first.
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyCompliancePage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Trust — agent trust scores + earned-autonomy tier management
              {
                path: 'trust',
                element: (
                  // routes.go replaced this resource's role check with
                  // RequirePermission(action, "trust") precisely because the
                  // old one "never called OPA, making a custom role's
                  // trust:read/manage grant inert" (#19). That fix shipped
                  // server-side; without the capabilities below the console
                  // still turned those grantees away at the door.
                  <RequireRole
                    roles={['tenant_owner', 'tenant_admin', 'platform_admin']}
                    // trust:read ONLY. TrustPage gates itself on trust:read
                    // (TrustPage.tsx:536) and the server maps list/read to
                    // trust:read alone, so a manage-only grantee would be let
                    // in and then refused by both.
                    anyCapability={['trust:read']}
                  >
                    <RouteSuspense><LazyTrustPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },

          // ── Cost hub (/tenant/cost/*) — unchanged, uses its own CostSubNav ─
          { path: 'cost', element: <RouteSuspense><LazyCostDashboard /></RouteSuspense> },
          {
            path: 'cost/chargeback',
            element: (
              // GAP 4 fix: chargeback is financial data and was missing the
              // RequireRole gate its sibling /cost/reports route has.
              // NOT the same role set as reports any more: reports also admits
              // tenant_billing_admin (see below). That role reaches this ledger by
              // other means anyway — the Monitor hub's chargeback CSV (export:cost)
              // and GET /costs/chargeback (read:cost) — so this guard is a UI-only
              // restriction for it. Left narrow deliberately rather than widened
              // without a decision; revisit together with that CSV button.
              <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                <RouteSuspense><LazyChargebackPage /></RouteSuspense>
              </RequireRole>
            ),
          },
          { path: 'cost/cost-centers', element: <RouteSuspense><LazyCostCentersPage /></RouteSuspense> },
          {
            path: 'cost/reports',
            element: (
              // platform_admin is in this list but cannot actually use the page:
              // deriveCapabilities gives it no billing:read (rbac.rego grants it no
              // "billing" key), so it passes the guard and lands on the page's own
              // "Access restricted" state. Pre-existing, and the reason the arm was
              // added — "so the platform Cost hub's Reports tab isn't a dead end" —
              // does not hold: /platform/cost/reports renders PlatformReportsPage.
              // Left in place rather than removed as out-of-scope churn; it grants
              // nothing.
              // tenant_billing_admin holds read:billing and export:cost in
              // rbac.rego — i.e. the server authorises every route this page
              // calls — and was the one role whose job the page IS. Without it
              // here, widening the billing:read capability achieved nothing:
              // RequireRole redirects before ReportsPage renders. The page's
              // Advanced workflow filter is separately gated on workflow:list,
              // which this role does NOT hold.
              <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin', 'tenant_billing_admin']}>
                <RouteSuspense><LazyReportsPage /></RouteSuspense>
              </RequireRole>
            ),
          },

          // ── Infrastructure hub (/tenant/infra/*) ──────────────────────────
          {
            path: 'infra',
            element: <RouteSuspense><LazyInfraHub /></RouteSuspense>,
            children: [
              // Default: redirect to Clusters
              { index: true, element: <HubIndexRedirect hubTo="/tenant/infra" /> },
              // Clusters
              { path: 'clusters', element: <RouteSuspense><LazyClustersPage /></RouteSuspense> },
              // Offline limits: owner/admin only, like the PUT routes behind it. Static segment, so it
              // ranks above clusters/:id.
              {
                path: 'clusters/offline-limits',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyAgentOfflineLimitsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              { path: 'clusters/:id', element: <RouteSuspense><LazyClusterDetail /></RouteSuspense> },
              // Branch Deployments was removed: old links land on Environments.
              { path: 'branch-deployments', loader: () => redirect('/tenant/infra/environments') },
              // Environments (RequireRole preserved)
              {
                path: 'environments',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'tenant_operator', 'platform_admin']}>
                    <RouteSuspense><LazyEnvironmentsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Connections (RequireRole preserved)
              {
                path: 'connections',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'tenant_operator', 'platform_admin']}>
                    <RouteSuspense><LazyConnectionsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Credentials (RequireRole preserved)
              {
                path: 'credentials',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'tenant_operator', 'platform_admin']}>
                    <RouteSuspense><LazyCredentialsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Secrets — tenant-scoped write-only credentials
              // (internal/api/v1/secret/handler.go). Owner/admin only,
              // matching the backend's OPA gate (rbac.rego:103, "secret"
              // actions restricted to tenant_owner/tenant_admin/platform_admin)
              // — narrower than Connections/Credentials above, same as
              // Secret Backends below.
              {
                path: 'secrets',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyTenantSecretsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Secret Backends (RequireRole preserved)
              {
                path: 'secret-backends',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazySecretBackendsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Notification Channels (RequireRole preserved)
              // Roles must match the backend gate exactly: /channels is
              // restricted server-side to tenant_owner/tenant_admin/
              // platform_admin (internal/api/v1/notification/handler_ext.go,
              // withRoleAuthz(..., "notification_channel", "tenant_owner",
              // "tenant_admin", "platform_admin")) -- tenant_operator has no
              // backend access to this domain at all, and platform_admin
              // must be able to reach it.
              {
                path: 'notification-channels',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyNotificationChannels /></RouteSuspense>
                  </RequireRole>
                ),
              },
              {
                path: 'notification-channels/new/:type',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyChannelWizard /></RouteSuspense>
                  </RequireRole>
                ),
              },
              {
                path: 'notification-channels/:id/edit',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyChannelWizard /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // Archival (#46) — same gate as Secret Backends/Notification
              // Channels (canManageTenantResources in navigation.ts).
              {
                path: 'archival',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyArchivalConfigPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },

          // ── Admin hub (/tenant/admin/*) ────────────────────────────────────
          {
            path: 'admin',
            element: <RouteSuspense><LazyAdminHub /></RouteSuspense>,
            children: [
              // M1: index must land on a tab EVERY tenant user can see. This was
              // "license", on the premise that License "has no capability gate" —
              // true of the ROUTE, false of the page: license:read excludes
              // tenant_viewer and the finance/approval roles, whose rbac.rego
              // "license" grant is an explicit empty list, so its panels render an
              // error card. Once the License TAB was narrowed to match that, those
              // six roles clicked Admin and landed on a page with no tab selected.
              // Tags is the only Admin tab gated on `p.isTenant` alone, and its
              // endpoints carry no role gate, so every tenant role can both see the
              // tab and use the page. navigation.rbac.test.tsx now asserts this
              // property for every hub index redirect.
              { index: true, element: <HubIndexRedirect hubTo="/tenant/admin" /> },
              // Users
              { path: 'users', element: <RouteSuspense><LazyUsersPage /></RouteSuspense> },
              // Roles — read-only capability matrix (Owner/Admin only)
              {
                path: 'roles',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin']}>
                    <RouteSuspense><LazyRolesPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              // License
              { path: 'license', element: <RouteSuspense><LazyLicenseDashboard /></RouteSuspense> },
              // License Usage — node-execution capacity metering (NE.10)
              { path: 'license/node-usage', element: <RouteSuspense><LazyNodeUsageDashboard /></RouteSuspense> },
              // Tags / Categories (formerly in Curate sidebar section)
              { path: 'tags', element: <RouteSuspense><LazyTagsPage /></RouteSuspense> },
              { path: 'categories', element: <RouteSuspense><LazyCategoriesPage /></RouteSuspense> },
              // Notification routing rules (moved from Operate per §7)
              {
                path: 'notification-rules',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyRoutingRulesListPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              {
                path: 'notification-rules/new',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyRuleBuilderCreatePage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              {
                path: 'notification-rules/:id/edit',
                element: (
                  <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                    <RouteSuspense><LazyRuleBuilderEditPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },

          // ── Deleted hub (/tenant/deleted) — unified soft-deleted item recycle bin ─
          {
            path: 'deleted',
            element: (
              <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                <RouteSuspense><LazyDeletedHub /></RouteSuspense>
              </RequireRole>
            ),
          },

          // ── Settings — stays at /tenant/settings (ux-settings regrouping in T2.3) ─
          { path: 'settings', element: <RouteSuspense><LazySettingsPage /></RouteSuspense> },
          {
            path: 'settings/sso',
            element: (
              <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                <RouteSuspense><LazySSOSettingsPage /></RouteSuspense>
              </RequireRole>
            ),
          },
          {
            path: 'settings/sso/drift',
            element: (
              <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                <RouteSuspense><LazySSODriftReport /></RouteSuspense>
              </RequireRole>
            ),
          },
          {
            path: 'settings/sso/access-review',
            element: (
              <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                <RouteSuspense><LazyAccessReviewPage /></RouteSuspense>
              </RequireRole>
            ),
          },
          // Outbound Channels settings (P2.359 — reshaped from the P2.337
          // "Outreach Settings" page, ADR-163) — per-channel sender/domain
          // caps + reply tracking, tenant-wide suppression list + kill
          // switch. Same role gate as the sibling settings/sso pages.
          {
            path: 'settings/outbound-channels',
            element: (
              <RequireRole roles={['tenant_owner', 'tenant_admin', 'platform_admin']}>
                <RouteSuspense><LazyOutboundChannelsPage /></RouteSuspense>
              </RequireRole>
            ),
          },
        ],
      },

      // Platform routes — tenant users redirected to /tenant/dashboard
      {
        path: 'platform',
        element: <PlatformOnly />,
        children: [
          { index: true, element: <RouteSuspense><LazyPlatformDashboard /></RouteSuspense> },
          { path: 'tenants', element: <RouteSuspense><LazyTenantsPage /></RouteSuspense> },
          { path: 'tenants/:id', element: <RouteSuspense><LazyTenantDetail /></RouteSuspense> },

          // ── Access hub (/platform/users/*) ────────────────────────────────
          {
            path: 'users',
            element: <RouteSuspense><LazyAccessHub /></RouteSuspense>,
            children: [
              { index: true, element: <RouteSuspense><LazyPlatformUsersPage /></RouteSuspense> },
            ],
          },
          {
            path: 'approvals',
            element: <RouteSuspense><LazyAccessHub /></RouteSuspense>,
            children: [
              { index: true, element: <RouteSuspense><LazyPlatformApprovalQueue /></RouteSuspense> },
              // Deep link (a settings panel's "Review in approvals", a notification): the queue focuses that request.
              { path: ':id', element: <RouteSuspense><LazyPlatformApprovalQueue /></RouteSuspense> },
              // An identifier declaration is approved here, with its attestation form (the card's "Review and attest" opens it).
              { path: ':id/review', element: <RouteSuspense><LazyIdentifierDeclarationReviewPage /></RouteSuspense> },
            ],
          },
          {
            path: 'impersonation',
            element: <RouteSuspense><LazyAccessHub /></RouteSuspense>,
            children: [
              {
                index: true,
                element: (
                  <RequireRole
                    roles={['platform_support_l1', 'platform_support_l2', 'platform_support_l3', 'platform_support_manager']}
                  >
                    <RouteSuspense><LazyImpersonationPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },

          { path: 'managed/clusters', element: <RouteSuspense><LazyManagedClustersPage /></RouteSuspense> },
          { path: 'managed/clusters/:id', element: <RouteSuspense><LazyManagedClusterDetail /></RouteSuspense> },
          {
            // Catalog hub: Items (index) · Registries (marketplace sync sources).
            path: 'catalog',
            element: <RouteSuspense><LazyPlatformCatalogHub /></RouteSuspense>,
            children: [
              { index: true, element: <RouteSuspense><LazyPlatformCatalogAdmin /></RouteSuspense> },
              { path: 'registries', element: <RouteSuspense><LazyMarketplaceRegistriesAdmin /></RouteSuspense> },
              // Declared identifier patterns of one catalog item (row action "Identifier declarations").
              { path: ':id/identifiers', element: <RouteSuspense><LazyIdentifierDeclarationPage /></RouteSuspense> },
              // Per-tool block / risk word / suggestion of one platform catalog item (ADR-335; row action "Tools and risk words").
              { path: ':id/tools', element: <RequireRole roles={['platform_admin']}><RouteSuspense><LazyCatalogToolEditorPage /></RouteSuspense></RequireRole> },
            ],
          },
          {
            path: 'connector-types',
            element: <RouteSuspense><LazyConnectorTypeAdmin /></RouteSuspense>,
          },

          // ── Cost hub (/platform/cost/*) ────────────────────────────────────
          {
            path: 'cost',
            element: <RouteSuspense><LazyPlatformCostHub /></RouteSuspense>,
            children: [
              { index: true, element: <RouteSuspense><LazyCostDashboard /></RouteSuspense> },
              // ADR-239 Phase 1 (DESIGN.md §4.3a/§4.3c, owner decisions 2 & 4):
              // these three used to mount the tenant-console pages verbatim
              // (ChargebackPage/CostCentersPage/ReportsPage), which 403'd a
              // platform admin — all three called /tenant/costs/* or
              // /tenant/reports*, tenant-context-only. Replaced with
              // grant-free, totals/status-only platform-native pages; line-
              // level detail and report generation/download/schedule are
              // reached only through RequiresGrantLink's impersonation-grant
              // flow. Gated by RequirePlatformRole (isPlatformUser) on the
              // backend, same as GetExecutionStats — all three platform
              // roles (admin/operator/viewer) may read these.
              { path: 'chargeback', element: <RouteSuspense><LazyPlatformChargebackSummary /></RouteSuspense> },
              { path: 'cost-centers', element: <RouteSuspense><LazyPlatformCostCentersSummary /></RouteSuspense> },
              { path: 'reports', element: <RouteSuspense><LazyPlatformReportsPage /></RouteSuspense> },
            ],
          },

          // ── Admin hub (/platform/settings/*) ───────────────────────────────
          {
            path: 'settings',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              { index: true, element: <RouteSuspense><LazySettingsPage /></RouteSuspense> },
            ],
          },
          {
            path: 'license',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              { index: true, element: <RouteSuspense><LazyLicenseDashboard /></RouteSuspense> },
            ],
          },
          {
            path: 'flags',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              {
                // FG-A3: read-only observability over GET /platform/flags, which is
                // RequirePlatformAdmin-gated on the backend (flags_handler.go).
                index: true,
                element: (
                  <RequireRole roles={['platform_admin']}>
                    <RouteSuspense><LazyPlatformFlagsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },
          {
            path: 'archival-settings',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              {
                // GET/PUT /platform/archival-settings is RequirePlatformAdmin-gated on the backend.
                index: true,
                element: (
                  <RequireRole roles={['platform_admin']}>
                    <RouteSuspense><LazyArchivalSettingsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },
          {
            path: 'email',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              {
                // GET/PUT/POST /platform/email is RequirePlatformAdmin-gated on the backend (ADR-339).
                index: true,
                element: (
                  <RequireRole roles={['platform_admin']}>
                    <RouteSuspense><LazyPlatformEmailPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },
          {
            path: 'connector-store',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              {
                // /admin/connectors/* (limits, signing, shared catalog) is RequirePlatformAdmin-gated on the backend.
                index: true,
                element: (
                  <RequireRole roles={['platform_admin']}>
                    <RouteSuspense><LazyConnectorStoreSettingsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },
          {
            // ADR-268: /platform/template-catalog/* is RequirePlatformAdmin-gated.
            path: 'template-catalog',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              {
                index: true,
                element: (
                  <RequireRole roles={['platform_admin']}>
                    <RouteSuspense><LazyPlatformTemplateCatalogPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },
          {
            // ADR-292: /platform/global-templates/* is RequirePlatformAdmin-gated on the backend.
            path: 'global-templates',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              {
                index: true,
                element: (
                  <RequireRole roles={['platform_admin']}>
                    <RouteSuspense><LazyGlobalTemplatesPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
              {
                // the checker's review page (deep-linked from the approval card)
                path: 'review/:revisionId',
                element: (
                  <RequireRole roles={['platform_admin']}>
                    <RouteSuspense><LazyGlobalTemplateReviewPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },
          {
            path: 'secrets',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              {
                // internal/api/v1/platformsecret/handler.go's routes are all
                // RequirePlatformAdmin-gated — mirror that exactly, same as
                // the Feature Flags route above.
                index: true,
                element: (
                  <RequireRole roles={['platform_admin']}>
                    <RouteSuspense><LazyPlatformSecretsPage /></RouteSuspense>
                  </RequireRole>
                ),
              },
            ],
          },
          {
            path: 'audit',
            element: <RouteSuspense><LazyPlatformAdminHub /></RouteSuspense>,
            children: [
              // ADR-239 Phase 1 (DESIGN.md §3.6/§4.3b, owner decision 3):
              // LazyAuditLogViewer used useTenantContext()-driven
              // useAuditEvents, hitting /tenant/audit — tenant-context-only,
              // 403 for a platform admin. Replaced with the grant-free,
              // 7-field metadata list; full event detail (payload/diff/actor
              // identity) is reached only via RequiresGrantLink.
              { index: true, element: <RouteSuspense><LazyPlatformAuditPage /></RouteSuspense> },
            ],
          },

          {
            path: 'notifications',
            element: (
              <RequireRole roles={['platform_admin']}>
                <RouteSuspense><LazyBroadcastComposerPage /></RouteSuspense>
              </RequireRole>
            ),
          },
          // LIVE-25: the bell's "View all notifications" had nowhere to land for
          // a platform-console user — /platform/notifications is (deliberately,
          // per navigation.ts's "Broadcasts" nav item) the broadcast COMPOSER,
          // not an inbox, and no platform-side inbox route existed at all. Reuses
          // the same NotificationFeed shell the tenant page mounts (its "My Feed"
          // tab already reads/writes through the SAME useNotifications/
          // useUnreadCount/useMarkAsRead/useMarkAllAsRead hooks the bell uses —
          // notificationApi.getBasePath() routes those to /platform/user-notifications
          // for a platform-console user regardless of which page called them, so
          // read-state stays in sync with the bell and mark-all-read works for the
          // tenant_id-NULL platform-level rows without any new API surface).
          // Open to every platform persona the bell itself renders for
          // (isPlatformConsole in NotificationPanel.tsx), not just platform_admin —
          // an operator/viewer/auditor's own alerts are theirs to read too.
          {
            path: 'notifications-inbox',
            element: (
              <RequireRole roles={PLATFORM_ROLES}>
                <RouteSuspense><LazyNotificationFeedPage /></RouteSuspense>
              </RequireRole>
            ),
          },
        ],
      },

      { path: '*', element: <NotFound /> },
    ],
  },
])

function App() {
  // AppShell (Cmd+K listener) and CommandPalette are rendered INSIDE Layout
  // because they use router hooks (useNavigate) and must live within
  // RouterProvider's tree when using createBrowserRouter.
  return <RouterProvider router={router} />
}

export default App
